import {
  CakeIcon,
  HeartIcon,
  HomeIcon,
  KeyIcon,
  ScaleIcon,
  ScissorsIcon,
} from '@heroicons/react/outline';
import Link from 'next/link';
import type { ComponentType, SVGProps } from 'react';

import agents from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { PhoneGlyph, useReceptionistT } from './kit';

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

/** The businesses that get the most calls they can't always answer. */
export default function ReceptionistIndustries() {
  const { t } = useReceptionistT();
  const { ref, state } = useReveal<HTMLUListElement>(0.12);

  const industries: {
    icon: Icon;
    name: string;
    detail: string;
    call: string;
  }[] = [
    {
      icon: HeartIcon,
      name: t('Clinics and dental offices'),
      detail: t(
        'Answers questions about opening hours, services and the insurance you accept, and books appointments.',
      ),
      call: t('Can I book a cleaning for next week?'),
    },
    {
      icon: ScissorsIcon,
      name: t('Salons and spas'),
      detail: t(
        'Books cuts, color and treatments, and tells callers what things cost and how long they take.',
      ),
      call: t('Do you have anything on Saturday morning?'),
    },
    {
      icon: HomeIcon,
      name: t('Home services'),
      detail: t(
        'For plumbers, electricians and HVAC technicians: it answers while you’re on a job, books visits and notes the details.',
      ),
      call: t('My heating stopped working. Can someone come today?'),
    },
    {
      icon: KeyIcon,
      name: t('Real estate'),
      detail: t(
        'Answers questions about your listings and books showings, whatever time buyers call.',
      ),
      call: t('Is the house on Elm Street still available?'),
    },
    {
      icon: CakeIcon,
      name: t('Restaurants and cafés'),
      detail: t(
        'Answers questions about opening hours, the menu and parking, and takes reservations.',
      ),
      call: t('Do you have a table for four at 7?'),
    },
    {
      icon: ScaleIcon,
      name: t('Law firms and agencies'),
      detail: t(
        'Answers first questions, takes the caller’s details and books consultations.',
      ),
      call: t('Can I book a first consultation this week?'),
    },
  ];

  return (
    <section id='industries' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='grid gap-8 lg:grid-cols-12 lg:items-end'>
          <SectionHeading
            className='lg:col-span-7'
            title={t('Built for businesses that live on the phone.')}
            tail={t('And can’t always pick up.')}
          />
          <p className={clsxm('text-lg leading-8 lg:col-span-5', tone.body)}>
            {t(
              'Give it your services, prices and policies, and it handles the calls your business gets every day.',
            )}
          </p>
        </div>

        <ul
          ref={ref}
          data-reveal={state}
          className={clsxm(
            agents.reveal,
            'mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
          )}
        >
          {industries.map(
            ({ icon: IndustryIcon, name, detail, call }, index) => (
              <li
                key={name}
                className={clsxm(
                  agents.rv,
                  'flex flex-col rounded-[22px] p-6 sm:p-7',
                  tone.surface,
                  tone.line,
                )}
                style={delay(index * 70)}
              >
                <span className='flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-50 text-primary-600 ring-1 ring-inset ring-primary-600/10 dark:bg-primary-400/10 dark:text-primary-300 dark:ring-primary-400/20'>
                  <IndustryIcon className='h-5 w-5' />
                </span>
                <h3
                  className={clsxm(
                    'mt-6 text-lg font-semibold tracking-[-0.01em]',
                    tone.ink,
                  )}
                >
                  {name}
                </h3>
                <p
                  className={clsxm(
                    'mt-2 flex-1 text-[15px] leading-7',
                    tone.body,
                  )}
                >
                  {detail}
                </p>
                <p
                  className={clsxm(
                    'mt-6 flex items-start gap-2.5 rounded-2xl bg-[#f4f4fb] px-3.5 py-3 text-[14px] leading-snug dark:bg-white/[0.04]',
                    tone.ink,
                  )}
                >
                  <PhoneGlyph className='mt-0.5 h-4 w-4 flex-none text-primary-600 dark:text-primary-400' />
                  <span>
                    <span className='sr-only'>{t('A caller asks:')} </span>
                    {call}
                  </span>
                </p>
              </li>
            ),
          )}
        </ul>

        <Link
          href='/chatbots'
          className='group mt-10 inline-flex items-center gap-2 text-[15px] font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200'
        >
          {t('See everything AI agents can do')}
          <ArrowGlyph className='transition-transform duration-200 group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5' />
        </Link>
      </div>
    </section>
  );
}
